import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useCumulativeSearchParams } from '../../../hooks/cumulativeSearchParams';
import { useStorage } from '../../../hooks/useStorage';
import { useFirstPathSegment } from '../../../hooks/useFirstPathSegment';
import { useCurrentUser } from '../../../hooks';
import { FilterDefinitions, FilterUpdateFn, ResolvedFilters } from './types';

export type UseFiltersStorage<T extends FilterDefinitions> = {
  filters: ResolvedFilters<T>;
  setFilters: FilterUpdateFn;
  searchTerm: string;
  setSearchTerm: (searchTerm: string | null) => void;
  forcedAt: number;
};

export const useFiltersStorage = <T extends FilterDefinitions>(
  definitions: T,
  searchTermKey: string | null,
  persistenceKey: string | undefined | null,
): UseFiltersStorage<T> => {
  const [searchParams, setSearchParams] = useCumulativeSearchParams();

  const [locallyStoredFilters, setLocallyStoredFilters] =
    useLocallyPersistedFilters(definitions, persistenceKey);

  const filters: ResolvedFilters<T> = useMemo(() => {
    /// Find the value(s) in the URL search params
    /// or fallback to the locally stored filter value.
    const findOrFallback = (key: string) => {
      const raw = searchParams.getAll(key);
      if (raw.length > 0) {
        return raw;
      }
      const fallback = locallyStoredFilters?.[key];
      return fallback === undefined ? [] : [fallback];
    };

    const resolved = {} as ResolvedFilters<T>;
    for (const key in definitions) {
      const raw = decode(findOrFallback(key));
      if (raw.length === 0) {
        continue;
      }

      // If we can't parse the value, log an error
      // but don't bail out entirely. Make a best
      // effort to parse the rest of the filters.
      const { label, schema } = definitions[key];
      // Most of the time, the schema expects a single value, so try that first.
      let result = schema.safeParse(raw.length === 1 ? raw[0] : raw);
      if (result.success) {
        // Ignore null (i.e. unset) filters, we only care
        // about active filters.
        if (result.data !== null) {
          resolved[key] = { label, value: result.data };
        }
      } else if (raw.length === 1) {
        const initialError = result.error;
        // We might have tried to parse a single value when we had an array schema,
        // so try again with the array, just in case.
        result = schema.safeParse(raw);
        if (result.success) {
          if (result.data !== null) {
            resolved[key] = { label, value: result.data };
          }
        } else {
          console.error(
            `Failed to parse filter "${key}" with value "${JSON.stringify(
              raw,
            )}". Attempted to interpret as single value and as array.`,
            initialError,
            result.error,
          );
        }
      } else {
        console.error(
          `Failed to parse filter "${key}" with value "${raw}".`,
          result.error,
        );
      }
    }

    return resolved;
  }, [definitions, searchParams, locallyStoredFilters]);

  useEffect(() => {
    const mapped: Record<string, Date | string> = {};
    // get stored filters
    const storedFiltersKeys = Object.keys(filters);

    if (storedFiltersKeys.length > 0) {
      // If there are stored filtes
      // they should take precedence
      storedFiltersKeys.forEach((key) => {
        const filter = filters[key];
        mapped[key] = encode(filter.value);
      });
    } else {
      // otherwise, use the default values
      for (const key in definitions) {
        const { defaultValue } = definitions[key];
        if (defaultValue !== undefined) {
          mapped[key] = encode(defaultValue);
        }
      }
    }

    // set all the filters to the url as params
    if (Object.keys(mapped).length > 0) {
      setSearchParams(mapped);
    }
  }, []);

  const setFilters: FilterUpdateFn = useCallback(
    (encodedFilters) => {
      // ReactHookForm seems to encode string values, so we need to decode them here
      // so that we don't double-encode them when setting the URL params or store
      // bad values in persistent storage.
      const filters = Object.fromEntries(
        Object.entries(encodedFilters).map(([key, value]) => [
          key,
          decode(value),
        ]),
      );
      for (const key in filters) {
        if (filters[key] === '' || filters[key] === undefined) {
          filters[key] = null;
        } else {
          filters[key] = encode(filters[key]);
        }
      }
      setSearchParams(filters);
      setLocallyStoredFilters((prev) => ({
        ...prev,
        ...filters,
      }));
    },
    [setSearchParams],
  );

  useEffect(() => {
    const toBeStored = {} as Record<string, unknown>;
    for (const key in filters) {
      const { value } = filters[key];
      if (value) {
        toBeStored[key] = value;
      }
    }
    setLocallyStoredFilters(toBeStored);
  }, [JSON.stringify(filters), setLocallyStoredFilters]);

  const searchTerm = (searchTermKey && searchParams.get(searchTermKey)) || '';
  const [forcedAt, setForcedAt] = useState(0);
  const setSearchTerm = useCallback(
    (searchTerm: string | null, forcedAt?: number) => {
      if (!searchTermKey) {
        console.warn(
          'setSearchTerm called without a searchTermKey. This will not update the URL.',
        );
        return;
      }
      setSearchParams({
        [searchTermKey]: searchTerm === '' ? null : searchTerm,
      });
      forcedAt && setForcedAt(forcedAt);
    },
    [setSearchParams, searchTermKey],
  );

  return useMemo(
    () => ({
      filters,
      setFilters,
      searchTerm,
      setSearchTerm,
      forcedAt,
    }),
    [filters, setFilters, searchTerm, setSearchTerm, forcedAt],
  );
};

const encode = (value: unknown) =>
  Array.isArray(value)
    ? value.map(encode)
    : typeof value === 'string'
    ? encodeURIComponent(value)
    : value;

const decode = (value: unknown) =>
  Array.isArray(value)
    ? value.map(decode)
    : typeof value === 'string'
    ? decodeURIComponent(value)
    : value;

const useLocallyPersistedFilters = <T extends FilterDefinitions>(
  definitions: T,
  overridePersistancekey?: string | null,
) => {
  const persistenceKey = usePersistenceKey(definitions, overridePersistancekey);
  return useStorage(sessionStorage, persistenceKey, storageSchema);
};

const storageSchema = z.record(z.string(), z.unknown());

/**
 * Given an object, this function generates a key based on the current URL path
 * and the keys of the object.
 */
const usePersistenceKey = (
  obj: Record<string, unknown>,
  override: string | null | undefined,
) => {
  const firstPathSegment = useFirstPathSegment();
  const { companyId: instanceId } = useCurrentUser();

  if (override === null) {
    return null;
  }

  const keys = Object.keys(obj).join('_');
  const prefix = `${instanceId}~FILTERS~`;
  const computed = `${prefix}${firstPathSegment}§${keys}`;
  return override === undefined ? computed : prefix + override;
};
